import type { ViewState } from "./canvas-coordinates";

// 共有 URL に「現在表示しているキャンバス領域」を保持するためのユーティリティ。
// クエリ文字列に中心ステージ座標 (cx, cy) とズーム値 (z) を埋め込み、貼り付け先のブラウザでも
// 同じ位置・倍率でキャンバスが復元できるようにする（Google Maps の URL に近いイメージ）。
//
// URL 形式: ?cx=<number>&cy=<number>&z=<number>
//   - cx/cy はステージ座標（10240x5760 範囲内の絶対座標）
//   - z はズーム倍率（ZOOM_MIN..ZOOM_MAX）

const PARAM_CX = "cx";
const PARAM_CY = "cy";
const PARAM_Z = "z";

export interface CanvasViewUrlState {
  /** ステージ座標における表示中心 X */
  readonly centerStageX: number;
  /** ステージ座標における表示中心 Y */
  readonly centerStageY: number;
  /** ズーム倍率 */
  readonly zoom: number;
}

/**
 * 現在の URL クエリから view 状態を読み出す。
 * 必要なパラメータが揃っていない、または数値解釈できない場合は null を返す。
 */
export function readCanvasViewFromUrl(search: string): CanvasViewUrlState | null {
  const params = new URLSearchParams(search);
  const cx = parseFloatParam(params.get(PARAM_CX));
  const cy = parseFloatParam(params.get(PARAM_CY));
  const z = parseFloatParam(params.get(PARAM_Z));
  if (cx === null || cy === null || z === null) return null;
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(z)) return null;
  if (z <= 0) return null;
  return { centerStageX: cx, centerStageY: cy, zoom: z };
}

/**
 * ViewState（panX/panY/zoom + viewport の寸法）から、表示中央のステージ座標を求める。
 * viewport 寸法は呼び出し元（CanvasStage）で window から取得して渡す前提とする。
 */
export function deriveCenterStageFromView(
  view: ViewState,
  viewport: { readonly width: number; readonly height: number }
): { readonly centerStageX: number; readonly centerStageY: number } {
  return {
    centerStageX: (viewport.width / 2 - view.panX) / view.zoom,
    centerStageY: (viewport.height / 2 - view.panY) / view.zoom,
  };
}

/**
 * 表示中央のステージ座標とズーム倍率から panX/panY を逆算する。
 */
export function deriveViewFromCenterStage(
  centerStageX: number,
  centerStageY: number,
  zoom: number,
  viewport: { readonly width: number; readonly height: number }
): ViewState {
  return {
    zoom,
    panX: viewport.width / 2 - centerStageX * zoom,
    panY: viewport.height / 2 - centerStageY * zoom,
  };
}

/**
 * URL クエリ文字列に cx/cy/z を反映した文字列を返す。
 * 他の既存パラメータは保持する。
 */
export function buildCanvasViewSearchString(
  baseSearch: string,
  state: CanvasViewUrlState
): string {
  const params = new URLSearchParams(baseSearch);
  params.set(PARAM_CX, formatNumber(state.centerStageX));
  params.set(PARAM_CY, formatNumber(state.centerStageY));
  params.set(PARAM_Z, formatNumber(state.zoom));
  const next = params.toString();
  return next.length > 0 ? `?${next}` : "";
}

function parseFloatParam(value: string | null): number | null {
  if (value === null) return null;
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return null;
  return num;
}

function formatNumber(value: number): string {
  // 過剰な小数桁による URL 肥大化を避けるため、ピクセル/倍率の精度として小数 2 桁に丸める。
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
